import { useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import api from "services/api";

import ErrorResponseDto from "dto/services/error-response-dto";
import PageArticleCategoryDto, { PageArticleCategoryTableCreatePayloadDto, PageArticleCategoryTableQueryParamsDto, PageArticleCategoryTableUpdatePayloadDto } from "dto/pages/article-category/page-artilcle-category-dto";

import getQueryParams from "common/get-query-params";
import encodeQueryParams from "common/encode-query-params";
import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import CONSTANTS from "constants/constant";

import ActionEnum from "enum/action";

import Button from "components/app-button";
import AppHeading from "components/app-heading";
import ArticleCategoryTable from "components/pages/article-category/app-article-category-table";

const PageArticleCategory = () => {
	const [disableAddNew, setDisableAddNew] = useState(false);
	const [newRow, setNewRow] = useState<PageArticleCategoryTableCreatePayloadDto>();
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const [isFetching, setIsFetching] = useState(false);
	const [categoryData, setCategoryData] = useState<PageArticleCategoryDto>();
	const queryParams = useRef(getQueryParams<PageArticleCategoryTableQueryParamsDto>(searchParams, { page: 0 }));

	const onHandleGetArticleCategory = useCallback(async () => {
		setIsFetching(true);

		try {
			const { page } = queryParams.current;
			const payload = { size: 10, page };
			const response = await api.get.article_category.category(payload);

			setCategoryData(response.data.data.list);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		} finally {
			setIsFetching(false);
		}
	}, []);

	//prettier-ignore
	const onHandleAddArticleCategory = useCallback(async (payload: PageArticleCategoryTableCreatePayloadDto) => {
		let response = null;

		try {
			await api.post.article_category.create(payload);

			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}

		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.CREATED, ActionEnum.Create);

			if (categoryData && categoryData?.numberOfElements === categoryData.size) {
				queryParams.current.page = categoryData.totalElements % categoryData.size === 0 ? categoryData.totalPages : categoryData.totalPages - 1;
			}

			onHandleGetArticleCategory();
		}
	}, [categoryData, onHandleGetArticleCategory]);

	//prettier-ignore
	const onUpdateArticleCategory = useCallback(async (payload: PageArticleCategoryTableUpdatePayloadDto, page? : number) => {
		let response = null;
		
		try {
			await api.put.article_category.update({ id: payload.id }, payload);
			
			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);

			if (typeof page === "number") {
				queryParams.current.page = page;
			}

			onHandleGetArticleCategory();
		}
	}, [onHandleGetArticleCategory]);

	const onHandlePageChange = (page: number) => {
		queryParams.current.page = page;

		memoSetSearchParams.current(encodeQueryParams(queryParams.current));

		onHandleGetArticleCategory();
	};

	const onHandleAddNew = () => {
		setDisableAddNew(true);
		setNewRow({ title: "", status: "active" });
	};

	useEffect(() => {
		onHandleGetArticleCategory();
	}, [onHandleGetArticleCategory]);

	return (
		<div className="page-article-category">
			<div className="article-category">
				<AppHeading title="Articles Categories">
					<AppHeading.Divider />
				</AppHeading>

				<div className="article-category__button">
					<Button label="Add New" className="addNewBtn" disabled={disableAddNew} onClick={onHandleAddNew} />
				</div>

				{/* prettier-ignore */}
				<ArticleCategoryTable data={categoryData?.content || []} setDisableAddNew={setDisableAddNew} newRow={newRow} setNewRow={setNewRow} onUpdateArticleCategory={onUpdateArticleCategory} onCreateArticleCategory={onHandleAddArticleCategory} onHandlePageChange={onHandlePageChange} fetching={isFetching} paginationTable={{currentPage: categoryData?.number || 0, totalPages: categoryData?.totalPages || 0, totalElements: categoryData?.totalElements || 0, numberOfElements: categoryData?.numberOfElements || 0, tableSize: categoryData?.size || 10 }} />
			</div>
		</div>
	);
};

export default PageArticleCategory;
